import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaCalculator, FaArrowRight, FaInfoCircle } from 'react-icons/fa';

gsap.registerPlugin(ScrollTrigger);

const DEBOUNCE_MS = 500;

const SectionPayback = () => {
  const sectionRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const cacheRef = useRef(new Map());
  const isFirstRenderRef = useRef(true);

  const [loanAmount, setLoanAmount] = useState(2000);
  const [loanTerm, setLoanTerm] = useState(12);

  const [weeklyDisplay, setWeeklyDisplay] = useState(64.61);
  const [fortnightlyDisplay, setFortnightlyDisplay] = useState(129.23);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stale, setStale] = useState(false);

  const calcType = useMemo(
    () => (loanAmount < 2000 ? 'SACC' : 'MACC'),
    [loanAmount]
  );

  const allTerms = [6, 12, 18, 24];

  useEffect(() => {
    if (calcType === 'SACC' && loanTerm > 12) {
      setLoanTerm(12);
    }
  }, [calcType, loanTerm]);

  const applyResult = useCallback((summary) => {
    if (!summary) return;

    if (summary.frequencyLabel?.toLowerCase().includes('fortnight')) {
      const weekly = summary.payment / 2;
      setWeeklyDisplay(Math.round(weekly * 100) / 100);
      setFortnightlyDisplay(summary.payment);
    } else {
      setWeeklyDisplay(Math.round(summary.payment * 100) / 100);
      setFortnightlyDisplay(null);
    }

    setStale(false);
  }, []);

  const fetchCalculation = useCallback(
    async (amount, term, cacheKey) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        const durationWeeks = Math.round(term * 4.33);

        const res = await fetch('/api/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            principal: amount,
            duration: durationWeeks,
          }),
          signal: controller.signal,
        });

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || 'Calculation failed');
        }

        const summary = data?.repaymentSummary;

        cacheRef.current.set(cacheKey, summary);

        applyResult(summary);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    },
    [applyResult]
  );

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    setStale(true);

    const cacheKey = `${loanAmount}_${loanTerm}`;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      const cached = cacheRef.current.get(cacheKey);

      if (cached) {
        applyResult(cached);
        return;
      }

      fetchCalculation(loanAmount, loanTerm, cacheKey);
    }, DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [loanAmount, loanTerm, fetchCalculation, applyResult]);

  useEffect(() => {
    gsap.fromTo(
      '.calculator-card',
      { x: 50, opacity: 0 },
      {
        x: 0,
        opacity: 1,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.calculator-card',
          start: 'top 80%',
        },
      }
    );
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-padding bg-gradient-to-br from-gray-50 to-white relative overflow-hidden"
    >
      <div className="absolute top-20 left-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-primary/10 text-primary font-semibold px-4 py-2 rounded-full text-sm mb-4">
              Transparent Pricing
            </span>

            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark mb-6">
              Estimate Your <span className="text-gradient">Repayments</span>
            </h2>

            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Most applications are assessed on the same day. Use our calculator to get an estimate of your weekly repayments.
            </p>
          </motion.div>

          <div className="calculator-card">
            <div className="glass-card p-8 md:p-10 relative">

              <div className="absolute top-4 right-4 text-xs font-bold px-3 py-1 rounded-full bg-primary/10 text-primary">
                {calcType}
              </div>

              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-secondary to-secondary-500 rounded-xl flex items-center justify-center">
                  <FaCalculator className="text-primary-800 text-xl" />
                </div>
                <h3 className="font-display font-bold text-xl text-dark">
                  Estimate Your Repayments
                </h3>
              </div>

              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-gray-600">Amount</label>
                  <span className="text-primary font-bold">
                    ${loanAmount.toLocaleString()}
                  </span>
                </div>

                <input
                  type="range"
                  min="2000"
                  max="5000"
                  step="100"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />

                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>$2,000</span>
                  <span>$5,000</span>
                </div>
              </div>

              <div className="mb-8">
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium text-gray-600">Term</label>
                  <span className="text-primary font-bold">{loanTerm} months</span>
                </div>

                <div className="grid grid-cols-4 gap-2">
                  {allTerms.map((term) => {
                    const disabled =
                      calcType === 'SACC' && term > 12;

                    return (
                      <button
                        key={term}
                        disabled={disabled}
                        onClick={() => !disabled && setLoanTerm(term)}
                        className={`py-2 rounded-lg text-sm font-medium transition-all ${
                          disabled
                            ? 'bg-gray-100 text-gray-300 cursor-not-allowed opacity-40'
                            : loanTerm === term
                            ? 'bg-primary text-white shadow-lg'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {term}m
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary to-primary-700 rounded-2xl p-6 text-white mb-6">
                <p className="text-sm opacity-80 mb-1">
                  Estimated Weekly Payment
                </p>

                <p
                  className={`text-4xl font-display font-bold transition-opacity ${
                    stale || loading ? 'opacity-60' : 'opacity-100'
                  }`}
                >
                  ${weeklyDisplay}/week
                </p>

                <div className="h-6 mt-1">
                  {calcType === 'SACC' && fortnightlyDisplay ? (
                    <p className="text-sm opacity-80">
                      Equivalent to ${fortnightlyDisplay} debited fortnightly
                    </p>
                  ) : (
                    <p className="text-sm opacity-0">placeholder</p>
                  )}
                </div>

                <p className="text-xs opacity-60 mt-2 flex items-center gap-1">
                  <FaInfoCircle />
                  Indicative only. Actual rates may vary.
                </p>

                {error && (
                  <p className="text-xs text-red-200 mt-2">
                    {error}
                  </p>
                )}
              </div>

              <a
                href="/costs"
                className="w-full bg-secondary hover:bg-secondary-400 text-primary-800 font-semibold py-4 px-6 rounded-full transition-all duration-300 flex items-center justify-center gap-2 group"
              >
                Go to Full Calculator
                <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
              </a>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default SectionPayback;
